import React, { forwardRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { DEFAULT_LOCALE, isSupportedLocale } from '../../config/localeConfig';
import { localizedPath } from '../../utils/localizedPath';
import { useLocale } from '../../hooks/useLocale';
import { isNonLocalizedPath } from '../routing/routeExclusions';

function isExternalUrl(value) {
  return /^(https?:)?\/\//i.test(value) || value.startsWith('mailto:') || value.startsWith('tel:');
}

function stripExistingLocalePrefix(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return '/';

  const [first, ...rest] = segments;
  if (isSupportedLocale(first)) {
    return rest.length ? `/${rest.join('/')}` : '/';
  }

  return pathname;
}

export const LocalizedLink = forwardRef(function LocalizedLink(
  {
    to,
    children,
    locale: forcedLocale,
    preserveSearch = false,
    preserveHash = false,
    ...props
  },
  ref
) {
  const location = useLocation();
  const params = useParams();
  let localeContext;

  try {
    localeContext = useLocale();
  } catch {
    localeContext = null;
  }

  const routeLocale = params.locale || params.lang;
  const activeLocale =
    (forcedLocale && isSupportedLocale(forcedLocale) && forcedLocale) ||
    localeContext?.locale ||
    (isSupportedLocale(routeLocale) ? routeLocale : null) ||
    DEFAULT_LOCALE;

  if (typeof to === 'string') {
    if (isExternalUrl(to)) {
      return (
        <a ref={ref} href={to} {...props}>
          {children}
        </a>
      );
    }

    if (to.startsWith('#') || isNonLocalizedPath(to)) {
      return (
        <Link ref={ref} to={to} {...props}>
          {children}
        </Link>
      );
    }

    const cleanPath = to.startsWith('/') ? to : `/${to}`;
    const pathWithoutLocale = stripExistingLocalePrefix(cleanPath);
    const finalPath = localizedPath(activeLocale, pathWithoutLocale);
    const search = preserveSearch ? location.search || '' : '';
    const hash = preserveHash ? location.hash || '' : '';

    return (
      <Link ref={ref} to={`${finalPath}${search}${hash}`} {...props}>
        {children}
      </Link>
    );
  }

  if (typeof to === 'object' && to !== null) {
    const pathname = to.pathname || '/';
    if (pathname.startsWith('#') || isNonLocalizedPath(pathname)) {
      return (
        <Link ref={ref} to={to} {...props}>
          {children}
        </Link>
      );
    }
    const pathWithoutLocale = stripExistingLocalePrefix(pathname);

    return (
      <Link
        ref={ref}
        to={{
          ...to,
          pathname: localizedPath(activeLocale, pathWithoutLocale),
          search: preserveSearch ? location.search || to.search || '' : to.search,
          hash: preserveHash ? location.hash || to.hash || '' : to.hash,
        }}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link ref={ref} to={localizedPath(activeLocale, '/')} {...props}>
      {children}
    </Link>
  );
});

export default LocalizedLink;
